import type { CameraState } from './camera';
import type { RenderItem } from './scene';

export interface GridSettings {
  enabled: boolean;
  kind: 'dots' | 'lines' | 'cells';
  step: number;
  color: string;
}

export interface ImageProvider {
  /** Decoded bitmap for `att:<sha>` sources, or null while loading (provider invalidates when ready). */
  get(src: string): ImageBitmap | HTMLImageElement | HTMLCanvasElement | OffscreenCanvas | null;
}

export interface FrameInput {
  camera: CameraState;
  items: RenderItem[];
  background: string;
  grid: GridSettings | null;
  images: ImageProvider;
  /** Text smaller than this (device px) is drawn as greeked bars (LOD). */
  textLodPx: number;
  /** Show colors as on a dark canvas (dark theme); images keep theirs. */
  darkInk?: boolean;
}

export interface BackendStats {
  drawCalls: number;
  instances: number;
  textures: number;
  textureBytes: number;
  /** Pending work deferred to next frames (e.g. text uploads). */
  pending: number;
}

/**
 * Isolated render adapter (§3.2, §11.2). WebGL 2 is primary, Canvas 2D is the fallback,
 * WebGPU can be added later behind the same interface.
 */
export interface RenderBackend {
  readonly kind: 'webgl2' | 'canvas2d' | 'webgpu';
  render(frame: FrameInput): void;
  resize(width: number, height: number, dpr: number): void;
  stats(): BackendStats;
  /** True when the GPU context is lost and the renderer should fall back. */
  readonly lost: boolean;
  dispose(): void;
}

/** Adaptive grid step so lines are never denser than `minPx` (CSS px). */
export function adaptiveGridStep(step: number, zoom: number, minPx = 8): number {
  let s = step;
  let guard = 0;
  while (s * zoom < minPx && guard++ < 40) s *= 5;
  while (s * zoom > minPx * 25 && s / 5 >= step && guard++ < 80) s /= 5;
  return s;
}

/** Positive modulo in float64 (used to keep GPU values small). */
export function fmod(a: number, b: number): number {
  const r = a % b;
  return r < 0 ? r + b : r;
}
